import { useEffect, useState, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Grid,
  Paper,
  Stack,
  IconButton,
  CircularProgress,
  Divider,
  Chip,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Button,
  Tooltip,
} from "@mui/material";
import {
  MoleCancelButton,
  MoleDialog,
  MoleDialogActions,
  MoleDialogHeader,
  useMoleThemeColor,
} from "../../../components/MoleDialogShell";
import {
  Close,
  ImageOutlined,
  Inventory2,
  LocalOffer,
  AttachMoney,
  CalendarMonth,
  Update,
  Category,
  Fullscreen,
  CloudUpload,
  PhotoCamera,
} from "@mui/icons-material";
import { Util } from "../../../util/Util";
import { API_BASE_URL } from "../../../util/constants";
import { compressImageForUpload } from "../../../util/compressImageForUpload";
import { ITEM_IMAGE_ROLES } from "../../../util/itemImageCache";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import { toast } from "react-toastify";

interface ItemImageModalProps {
  isOpen: boolean;
  toggle: () => void;
  item: any;
  /** Enable per-role image upload / replace */
  allowUpload?: boolean;
  /** Called after a successful upload (blob available for local cache refresh) */
  onImageUpdated?: (itemId: number, role: string, blob: Blob | null) => void | Promise<void>;
}

const IMAGE_ROLES = ITEM_IMAGE_ROLES;

const ItemImageModal = ({
  isOpen,
  toggle,
  item,
  allowUpload = false,
  onImageUpdated,
}: ItemImageModalProps) => {
  const { t } = useTranslation("common");
  const { api } = useAuth();
  const themeColor = useMoleThemeColor();
  const [availableImages, setAvailableImages] = useState<{ role: string; url: string }[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [probing, setProbing] = useState(true);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [uploadingRole, setUploadingRole] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingRoleRef = useRef<string>("main");

  const itemId = item?.id || item?.item_id;

  const getImageUrl = useCallback((role: string, bustCache?: boolean) => {
    const id = item?.id || item?.item_id || item?.code || "";
    if (!id) return "";
    const base = `${API_BASE_URL}uploads/items/${id}-${role}.jpg`;
    return bustCache ? `${base}?t=${Date.now()}` : base;
  }, [item]);

  const probeImages = useCallback(() => {
    if (!item) return;

    setProbing(true);
    setSelectedIndex(0);
    setAvailableImages([]);

    const probePromises = IMAGE_ROLES.map((role) => {
      return new Promise<{ role: string; url: string } | null>((resolve) => {
        const img = new Image();
        const url = getImageUrl(role, true);
        img.onload = () => resolve({ role, url });
        img.onerror = () => resolve(null);
        img.src = url;
      });
    });

    Promise.all(probePromises).then((results) => {
      const found = results.filter(Boolean) as { role: string; url: string }[];
      setAvailableImages(found);
      setProbing(false);
    });
  }, [item, getImageUrl]);

  useEffect(() => {
    if (!isOpen || !item) return;
    probeImages();
  }, [isOpen, item, probeImages]);

  useEffect(() => {
    if (!isOpen || availableImages.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % availableImages.length);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + availableImages.length) % availableImages.length);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, availableImages.length]);

  const openFilePicker = (role: string) => {
    pendingRoleRef.current = role;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !itemId) return;

    const role = pendingRoleRef.current || "main";
    setUploadingRole(role);

    try {
      const optimized = await compressImageForUpload(file);
      const form = new FormData();
      form.append("image", optimized);
      form.append("role", role);

      const res = await api.post(`items/${itemId}/image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res && (res.status === 200 || res.status === 201)) {
        toast.success(t("productCatalog.uploadSuccess", { defaultValue: "Image updated" }));
        const url = getImageUrl(role, true);
        setAvailableImages((prev) => {
          const without = prev.filter((img) => img.role !== role);
          const next = [...without, { role, url }];
          // keep role order from IMAGE_ROLES
          next.sort(
            (a, b) => IMAGE_ROLES.indexOf(a.role) - IMAGE_ROLES.indexOf(b.role),
          );
          return next;
        });
        setSelectedIndex((prev) => {
          const nextList = (() => {
            const without = availableImages.filter((img) => img.role !== role);
            const next = [...without, { role, url }];
            next.sort(
              (a, b) => IMAGE_ROLES.indexOf(a.role) - IMAGE_ROLES.indexOf(b.role),
            );
            return next;
          })();
          const idx = nextList.findIndex((img) => img.role === role);
          return idx >= 0 ? idx : prev;
        });

        await onImageUpdated?.(Number(itemId), role, optimized);
      } else {
        toast.error(t("productCatalog.uploadFailed", { defaultValue: "Image upload failed" }));
      }
    } catch {
      toast.error(t("productCatalog.uploadFailed", { defaultValue: "Image upload failed" }));
    } finally {
      setUploadingRole(null);
    }
  };

  if (!item) return null;

  const selectedImage = availableImages[selectedIndex];
  const missingRoles = IMAGE_ROLES.filter(
    (role) => !availableImages.some((img) => img.role === role),
  );

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        hidden
        onChange={handleFileChange}
      />

      <MoleDialog
        open={isOpen}
        onClose={toggle}
        maxWidth="md"
        fullWidth
        themeColor={themeColor}
        paperOptions={{ maxHeight: "95vh" }}
      >
        <MoleDialogHeader
          eyebrow={t("inventory.itemGallery")}
          title={item.name || item.item_name || item.description || t("tables.item")}
          subtitle={item.item_code || item.code ? `${t("report.itemCode")}: ${item.item_code || item.code}` : undefined}
          icon={<ImageOutlined />}
          onClose={toggle}
          closeLabel={t("ui.buttons.close")}
          themeColor={themeColor}
        />

        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                  position: "relative",
                  height: { xs: 340, sm: 440 },
                  bgcolor: "action.hover",
                }}
              >
                {probing || uploadingRole ? (
                  <Stack height="100%" alignItems="center" justifyContent="center">
                    <CircularProgress size={48} />
                    {uploadingRole && (
                      <Typography variant="caption" color="text.secondary" mt={1}>
                        {t("productCatalog.uploading", { defaultValue: "Uploading…" })} ({uploadingRole})
                      </Typography>
                    )}
                  </Stack>
                ) : selectedImage ? (
                  <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                    <Box
                      component="img"
                      src={selectedImage.url}
                      alt={`${selectedImage.role} view`}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        p: 2,
                        transition: "transform 0.3s",
                        "&:hover": { transform: "scale(1.04)" },
                      }}
                    />
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                      {allowUpload && (
                        <Tooltip title={t("productCatalog.replaceImage", { defaultValue: "Replace image" })}>
                          <IconButton
                            onClick={() => openFilePicker(selectedImage.role)}
                            sx={{
                              bgcolor: "rgba(0,0,0,0.4)",
                              color: "white",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                            }}
                          >
                            <CloudUpload />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        onClick={() => setFullscreenOpen(true)}
                        sx={{
                          bgcolor: "rgba(0,0,0,0.4)",
                          color: "white",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                        }}
                      >
                        <Fullscreen />
                      </IconButton>
                    </Stack>
                  </Box>
                ) : (
                  <Stack height="100%" alignItems="center" justifyContent="center" spacing={1}>
                    <ImageOutlined sx={{ fontSize: 64, color: "text.disabled" }} />
                    <Typography color="text.secondary">
                      {t("inventory.noImagesAvailable")}
                    </Typography>
                    {allowUpload && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<PhotoCamera />}
                        onClick={() => openFilePicker("main")}
                      >
                        {t("productCatalog.uploadMain", { defaultValue: "Upload main image" })}
                      </Button>
                    )}
                  </Stack>
                )}
              </Paper>

              {availableImages.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                  <Chip
                    size="small"
                    label={t("inventory.imageCount", {
                      current: selectedIndex + 1,
                      total: availableImages.length,
                    })}
                  />
                </Box>
              )}

              {/* Existing thumbs */}
              {availableImages.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <ImageList cols={5} gap={8} sx={{ overflowX: "auto", pb: 1 }}>
                    {availableImages.map(({ role, url }, index) => (
                      <ImageListItem
                        key={role}
                        sx={{
                          cursor: "pointer",
                          border: selectedIndex === index ? "2px solid" : "2px solid transparent",
                          borderColor: selectedIndex === index ? "primary.main" : "transparent",
                          borderRadius: 1.5,
                          overflow: "hidden",
                          opacity: selectedIndex === index ? 1 : 0.75,
                          transition: "all 0.2s",
                          "&:hover": { opacity: 1 },
                        }}
                        onClick={() => setSelectedIndex(index)}
                      >
                        <img
                          src={url}
                          alt={t(`inventory.imageRole.${role}`, role)}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: 80,
                            objectFit: "cover",
                          }}
                        />
                        <ImageListItemBar
                          position="bottom"
                          sx={{
                            background:
                              "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, transparent 100%)",
                            py: 0.5,
                          }}
                          title={
                            <Typography variant="caption" sx={{ textTransform: "capitalize" }}>
                              {t(`inventory.imageRole.${role}`, role.replace("-", " "))}
                            </Typography>
                          }
                          actionIcon={
                            allowUpload ? (
                              <IconButton
                                size="small"
                                sx={{ color: "white" }}
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  openFilePicker(role);
                                }}
                              >
                                <CloudUpload fontSize="small" />
                              </IconButton>
                            ) : undefined
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}

              {/* Missing role slots for upload */}
              {allowUpload && missingRoles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="overline" color="text.secondary" fontWeight={700}>
                    {t("productCatalog.addViews", { defaultValue: "Add image views" })}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                    {missingRoles.map((role) => (
                      <Chip
                        key={role}
                        icon={<PhotoCamera fontSize="small" />}
                        label={t(`inventory.imageRole.${role}`, role.replace("-", " "))}
                        onClick={() => openFilePicker(role)}
                        variant="outlined"
                        color="primary"
                        sx={{ textTransform: "capitalize" }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="overline" color="text.secondary" fontWeight={700}>
                    {t("inventory.basicInformation")}
                  </Typography>
                  <Stack spacing={1.5} mt={1}>
                    <DetailRow
                      icon={<Inventory2 sx={{ fontSize: 16, color: "text.secondary" }} />}
                      label={t("inventory.currentStock")}
                      value={Util.formatNumber(item.stock || item.quantity || 0)}
                    />
                    <DetailRow
                      icon={<AttachMoney sx={{ fontSize: 16, color: "text.secondary" }} />}
                      label={t("inventory.sellingPrice")}
                      value={Util.formatNumber(item.price || item.selling_price || 0)}
                    />
                    <DetailRow
                      icon={<LocalOffer sx={{ fontSize: 16, color: "text.secondary" }} />}
                      label={t("report.itemCode")}
                      value={item.item_code || item.code || "—"}
                    />
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="overline" color="text.secondary" fontWeight={700}>
                    {t("inventory.classification")}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" mt={1} gap={1}>
                    {item.category && (
                      <Chip
                        icon={<Category fontSize="small" />}
                        label={item.category}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {item.family && <Chip label={item.family} variant="outlined" />}
                    {item.sector && <Chip label={item.sector} color="secondary" variant="outlined" />}
                    {item.subcategory && <Chip label={item.subcategory} variant="outlined" />}
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="overline" color="text.secondary" fontWeight={700}>
                    {t("inventory.timeline")}
                  </Typography>
                  <Stack spacing={1.5} mt={1}>
                    <DetailRow
                      icon={<CalendarMonth sx={{ fontSize: 16, color: "text.secondary" }} />}
                      label={t("tableView.detailPanel.created")}
                      value={Util.formatDate(item.created_at || item.created, "full")}
                    />
                    <DetailRow
                      icon={<Update sx={{ fontSize: 16, color: "text.secondary" }} />}
                      label={t("tableView.lastUpdated")}
                      value={Util.formatDate(item.updated_at || item.updated, "full")}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>

        <MoleDialogActions>
          {allowUpload && selectedImage && (
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => openFilePicker(selectedImage.role)}
              disabled={Boolean(uploadingRole)}
            >
              {t("productCatalog.replaceImage", { defaultValue: "Replace image" })}
            </Button>
          )}
          <MoleCancelButton onClick={toggle}>{t("ui.buttons.close")}</MoleCancelButton>
        </MoleDialogActions>
      </MoleDialog>

      <Dialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        fullScreen
        PaperProps={{ sx: { bgcolor: "rgba(0,0,0,0.95)" } }}
      >
        <IconButton
          onClick={() => setFullscreenOpen(false)}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "white",
            zIndex: 1,
          }}
        >
          <Close />
        </IconButton>
        {selectedImage && (
          <Box
            component="img"
            src={selectedImage.url}
            alt={selectedImage.role}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              p: 4,
            }}
          />
        )}
      </Dialog>
    </>
  );
};

const DetailRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    {icon}
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
      {label}:
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value || "—"}
    </Typography>
  </Stack>
);

export default ItemImageModal;
